import {
    PARAMETER_COLORS,
    type ParameterCategory,
    type ParameterItem,
} from "components/CC/UI/ParameterPicker/ParameterPicker";
import { $createFunctionPillNode, $isFunctionPillNode } from "components/CC/UI/TagInput/FunctionPillNode";
import {
    type GhostState,
    type PrefixValidationLookup,
    type InsertRawTextPayload,
    type Segment,
    type TagInputMode,
    type TagParamType,
    type TagSegment,
    type ValidationLookup,
} from "components/CC/UI/TagInput/tagInputTypes";
import { $createTagNode, $isTagNode } from "components/CC/UI/TagInput/TagNode";
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $isElementNode,
    $isLineBreakNode,
    $isTextNode,
    type EditorState,
    type ElementNode,
    type LexicalEditor,
    type LexicalNode,
} from "lexical";

// ============================================================================
// Focus helpers
// ============================================================================

export const resolveTargetElement = (target: EventTarget | null): HTMLElement | null => {
    if (target instanceof HTMLElement) return target;
    if (target instanceof Node) return target.parentElement;
    return null;
};

export const resolveFocusableTarget = (target: EventTarget | null): HTMLElement | null => {
    const targetElement = resolveTargetElement(target);
    if (!targetElement) return null;

    const tagInputContainer = targetElement.closest("[data-tag-text-input-container='true']");
    if (tagInputContainer instanceof HTMLElement) {
        const editorRoot = tagInputContainer.querySelector('[contenteditable="true"]');
        if (editorRoot instanceof HTMLElement) return editorRoot;
    }

    return targetElement.closest(
        'input, textarea, select, button, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
    );
};

export const focusEditorAtEnd = (editor: LexicalEditor | null): void => {
    if (!editor) return;
    editor.update(() => {
        $getRoot().selectEnd();
    });
    editor.focus();
};

const TAG_ONLY_PLACEHOLDER = "{}";

/**
 * tag-only mode: pre-fill an empty editor with "{}" and place the caret between the braces,
 * so the user immediately sees they must type inside the braces (or pick from the popover).
 */
export const insertTagOnlyPlaceholder = (editor: LexicalEditor): void => {
    editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const textNode = $createTextNode(TAG_ONLY_PLACEHOLDER);
        paragraph.append(textNode);
        root.append(paragraph);
        textNode.select(1, 1);
    });
};

// TagNode is a DecoratorNode, whose getTextContent() is "" by default — so root.getTextContent()
// can't be trusted to detect a lone tag. Recurse and treat any TagNode as content.
const hasRenderableContent = (node: LexicalNode): boolean => {
    if ($isTagNode(node)) return true;
    if ($isTextNode(node)) return node.getTextContent().length > 0;
    if ($isElementNode(node)) return node.getChildren().some(hasRenderableContent);
    return false;
};

export const isEditorEmpty = (editor: LexicalEditor): boolean => {
    let empty = true;
    editor.getEditorState().read(() => {
        empty = !$getRoot().getChildren().some(hasRenderableContent);
    });
    return empty;
};

/** True when the editor still contains only the untouched tag-only placeholder "{}". */
export const isUntouchedTagOnlyPlaceholder = (editor: LexicalEditor): boolean => {
    let isPlaceholder = false;
    editor.getEditorState().read(() => {
        const paragraphs = $getRoot().getChildren();
        if (paragraphs.length !== 1) return;
        const paragraph = paragraphs[0];
        if (!$isElementNode(paragraph)) return;
        const children = paragraph.getChildren();
        isPlaceholder =
            children.length === 1 && $isTextNode(children[0]) && children[0].getTextContent() === TAG_ONLY_PLACEHOLDER;
    });
    return isPlaceholder;
};

// ============================================================================
// Parse / color helpers
// ============================================================================

// Context prefixes that carry transport info but don't determine the value type.
const CONTEXT_PREFIXES = ["parsedBody.", "query.", "headers."];

// Static Functions-tab names (Format & convert, Math Functions, Operators) —
// no backend yet, so these are matched by name rather than a runtime lookup.
export const FORMAT_CONVERT_NAMES = new Set(["toBool", "toFloat", "toInt", "toString"]);
export const MATH_FUNCTION_NAMES = new Set(["rand", "round", "min", "max", "sum"]);
const OPERATOR_NAMES = new Set(["+", "-", "<", ">", "<=", ">=", "=", "!=", "/", "*", "mod"]);

/** The paramType of a known function-call name (format_convert/math_functions), or undefined. */
export const getFunctionParamType = (name: string): "format_convert" | "math_functions" | undefined => {
    if (FORMAT_CONVERT_NAMES.has(name)) return "format_convert";
    if (MATH_FUNCTION_NAMES.has(name)) return "math_functions";
    return undefined;
};

export const isKnownFunctionName = (name: string): boolean => getFunctionParamType(name) !== undefined;

/**
 * Strips a leading context prefix (parsedBody., query., headers.) to expose the base key.
 * e.g. "parsedBody.data_surname" → "data_surname", "query.page" → "page"
 */
export const stripContextPrefix = (inner: string): string => {
    for (const prefix of CONTEXT_PREFIXES) {
        if (inner.startsWith(prefix)) return inner.slice(prefix.length);
    }
    return inner;
};

/**
 * Classifies a tag's inner value into a paramType based on its base key:
 *   data_*    → form_fields  (regardless of context prefix)
 *   _N        → rows         (row reference, e.g. _1, _2)
 *   system.*  → system
 *   steps.*   → steps
 *   otherwise → keys         (lookup in resolveTagValidation will further refine)
 */
export const inferParamType = (inner: string): Exclude<TagParamType, undefined> => {
    if (FORMAT_CONVERT_NAMES.has(inner)) return "format_convert";
    if (MATH_FUNCTION_NAMES.has(inner)) return "math_functions";
    if (OPERATOR_NAMES.has(inner)) return "operators";
    const base = stripContextPrefix(inner);
    if (base.startsWith("data_")) return "form_fields";
    if (/^_\d+/.test(base)) return "rows";
    const topPrefix = inner.split(".")[0];
    if (topPrefix === "system") return "system";
    if (topPrefix === "steps") return "steps";
    if (topPrefix === "parsedBody" || topPrefix === "query" || topPrefix === "headers") return "http_steps";
    if (inner === "status") return "http_steps";
    // First segment is likely a step ID — strip it and re-infer.
    const dotIndex = inner.indexOf(".");
    if (dotIndex !== -1) {
        const nestedType = inferParamType(inner.slice(dotIndex + 1));
        return nestedType === "keys" ? "steps" : nestedType;
    }
    return "keys";
};

// Case-sensitive on purpose: the backend TagReplacer (Tag::normalizeTagName) matches tag
// names exactly, so a differently-cased tag (e.g. "{AffS1}" vs the real "{affs1}") fails to
// resolve at runtime. Coloring/validating it as a match here would hide that mismatch.
export const normalizeLookupKey = (value: string): string => value.trim();

export const getRawTextInsertPayload = (
    selectValue: string,
    item: Pick<ParameterItem, "id" | "paramType" | "insertAsRawText" | "insertAsRawContainer">
): InsertRawTextPayload | null => {
    if (item.insertAsRawText) {
        const text = `{${item.id}}`;
        return { text, cursorOffset: text.length - 1 };
    }

    if (item.paramType === "format_convert" || item.paramType === "math_functions") {
        const text = `${item.id}()`;
        return { text, cursorOffset: text.length - 1 };
    }

    if (item.insertAsRawContainer) {
        const text = `{${selectValue}.}`;
        return { text, cursorOffset: text.length - 1 };
    }

    return null;
};

export const parseSegments = (
    value: string,
    displayLabelLookup?: DisplayLabelLookup,
    stepLabelLookup?: StepLabelLookup
): Segment[] => {
    if (!value) return [];
    const segments: Segment[] = [];
    const regex = /\{([^{}]+)\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(value)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }
        const inner = match[1];
        const paramType = inferParamType(inner);
        const displayLabel = resolveDisplayLabel(inner, displayLabelLookup, stepLabelLookup) ?? inner;
        segments.push({ type: "tag", inner, displayLabel, paramType });
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < value.length) {
        segments.push({ type: "text", value: value.slice(lastIndex) });
    }

    return segments;
};

export const getTagPillColor = (paramType: TagSegment["paramType"] | GhostState["paramType"]): string => {
    if (paramType === "form_fields") return PARAMETER_COLORS.form_fields;
    if (paramType === "rows") return PARAMETER_COLORS.rows;
    if (paramType === "system") return PARAMETER_COLORS.system;
    if (paramType === "steps") return PARAMETER_COLORS.steps;
    if (paramType === "http_steps") return PARAMETER_COLORS.http_steps;
    if (paramType === "formulas") return PARAMETER_COLORS.formulas;
    if (paramType === "format_convert") return PARAMETER_COLORS.format_convert;
    if (paramType === "math_functions") return PARAMETER_COLORS.math_functions;
    if (paramType === "operators") return PARAMETER_COLORS.operators;
    return PARAMETER_COLORS.keys;
};

// ============================================================================
// Validation helpers
// ============================================================================

export const flattenItems = (items: ParameterItem[]): ParameterItem[] => {
    return items.flatMap((item) => [item, ...(item.children ? flattenItems(item.children) : [])]);
};

// Color priority when a key appears in multiple categories: steps → keys → system → form_fields → rows → functions
const LOOKUP_PRIORITY: ParameterCategory["type"][] = [
    "steps",
    "keys",
    "system",
    "form_fields",
    "rows",
    "formulas",
    "format_convert",
    "math_functions",
    "operators",
];

export const buildValidationLookup = (categories: ParameterCategory[]): ValidationLookup => {
    const lookup: ValidationLookup = new Map();

    const sorted = [...categories].sort((a, b) => {
        const ai = LOOKUP_PRIORITY.indexOf(a.type);
        const bi = LOOKUP_PRIORITY.indexOf(b.type);
        return (ai === -1 ? LOOKUP_PRIORITY.length : ai) - (bi === -1 ? LOOKUP_PRIORITY.length : bi);
    });

    sorted.forEach((category) => {
        const defaultType =
            category.type === "form_fields" ||
            category.type === "keys" ||
            category.type === "rows" ||
            category.type === "system" ||
            category.type === "steps" ||
            category.type === "formulas" ||
            category.type === "format_convert" ||
            category.type === "math_functions" ||
            category.type === "operators"
                ? category.type
                : undefined;
        if (!defaultType) return;

        flattenItems(category.items).forEach((item) => {
            const paramType = item.paramType ?? defaultType;
            if (item.value !== undefined) {
                const vk = normalizeLookupKey(item.value);
                if (!lookup.has(vk)) lookup.set(vk, paramType);
            }
            const ik = normalizeLookupKey(item.id);
            if (!lookup.has(ik)) lookup.set(ik, paramType);
        });
    });

    return lookup;
};

/**
 * Collects items flagged `matchByPrefix: true` (e.g. "custom_") into a prefix list, so any tag
 * starting with that literal prefix — not just an exact match — validates and colors as this
 * item's paramType. Used by resolveTagValidation as a fallback after the exact-match lookup.
 */
export const buildPrefixValidationLookup = (categories: ParameterCategory[]): PrefixValidationLookup => {
    const prefixes: PrefixValidationLookup = [];

    categories.forEach((category) => {
        const defaultType =
            category.type === "form_fields" ||
            category.type === "keys" ||
            category.type === "rows" ||
            category.type === "system" ||
            category.type === "steps" ||
            category.type === "formulas"
                ? category.type
                : undefined;
        if (!defaultType) return;

        flattenItems(category.items).forEach((item) => {
            if (!item.matchByPrefix) return;
            prefixes.push({ prefix: item.id, paramType: item.paramType ?? defaultType });
        });
    });

    return prefixes;
};

export type DisplayLabelLookup = Map<string, string>;
export type NodeTypeLookup = Map<string, string>;
export type StepLabelLookup = Map<string, string>;
export type StepNodeTypeLookup = Map<string, string>;

// parsedBody/query/headers carry freeform keys (arbitrary header/query/body field
// names) that can't be checked against a known lookup — any sub-path under them is valid by shape
// alone, whether or not it's prefixed by a step id (e.g. "parsedBody.foo" or
// "<stepId>.parsedBody.foo"). "status" is a single fixed leaf, not a freeform container, so it
// keeps requiring an exact previous-step lookup match.
const HTTP_TRANSPORT_CONTAINER_ROOTS = new Set(["parsedBody", "query", "headers"]);

/**
 * Builds a lookup from stored tag inner value → display label.
 * Only items that have both `value` and `displayLabel` set are included
 * (currently only step parameters).
 */
export const buildDisplayLabelLookup = (categories: ParameterCategory[]): DisplayLabelLookup => {
    const lookup: DisplayLabelLookup = new Map();
    categories.forEach((category) => {
        flattenItems(category.items).forEach((item) => {
            const inner = item.value ?? item.id;
            if (item.displayLabel !== undefined) {
                lookup.set(normalizeLookupKey(inner), item.displayLabel);
            }
        });
    });
    return lookup;
};

/**
 * Maps each previous-step id to its display label (e.g. "1 - HTTP Source"), read from the
 * top-level "steps" category items.
 */
export const buildStepLabelLookup = (categories: ParameterCategory[]): StepLabelLookup => {
    const lookup: StepLabelLookup = new Map();
    categories.forEach((category) => {
        if (category.type !== "steps") return;
        category.items.forEach((item) => {
            lookup.set(item.id, item.name);
        });
    });
    return lookup;
};

/**
 * Resolves a tag's display label: the static per-item lookup first, then — for freeform
 * parsedBody/query/headers sub-paths that have no static picker entry (e.g. a header
 * name the user typed by hand) — composes one from the owning step's label plus the container and
 * sub-path, e.g. "1 - HTTP · headers · x-foo". A container path with no step-id prefix (e.g.
 * "parsedBody.event.id", typed by hand into the raw text the General section's parsedBody/query/
 * headers items insert) is scoped to the synthetic "general" step instead, e.g. "General · parsedBody · event.id".
 */
export const resolveDisplayLabel = (
    inner: string,
    displayLabelLookup?: DisplayLabelLookup,
    stepLabelLookup?: StepLabelLookup
): string | undefined => {
    const direct = displayLabelLookup?.get(normalizeLookupKey(inner));
    if (direct) return direct;
    if (!stepLabelLookup) return undefined;

    const segments = inner.split(".");

    if (segments.length >= 2 && HTTP_TRANSPORT_CONTAINER_ROOTS.has(segments[0])) {
        const [container, ...rest] = segments;
        const stepLabel = stepLabelLookup.get("general");
        if (stepLabel) return `${stepLabel} · ${container} · ${rest.join(".")}`;
    }

    if (segments.length < 3) return undefined;
    const [stepId, container, ...rest] = segments;
    if (!HTTP_TRANSPORT_CONTAINER_ROOTS.has(container)) return undefined;
    const stepLabel = stepLabelLookup.get(stepId);
    if (!stepLabel) return undefined;
    return `${stepLabel} · ${container} · ${rest.join(".")}`;
};

/**
 * Builds a lookup from stored tag inner value → nodeType string.
 * Used to restore the node icon on tags loaded from a raw value string.
 */
export const buildNodeTypeLookup = (categories: ParameterCategory[]): NodeTypeLookup => {
    const lookup: NodeTypeLookup = new Map();
    categories.forEach((category) => {
        flattenItems(category.items).forEach((item) => {
            const inner = item.value ?? item.id;
            if (item.nodeType !== undefined) {
                lookup.set(normalizeLookupKey(inner), item.nodeType);
            }
        });
    });
    return lookup;
};

/**
 * Maps each previous-step id to its nodeType (e.g. NodeType.Http), read from the top-level
 * "steps" category items.
 */
export const buildStepNodeTypeLookup = (categories: ParameterCategory[]): StepNodeTypeLookup => {
    const lookup: StepNodeTypeLookup = new Map();
    categories.forEach((category) => {
        if (category.type !== "steps") return;
        category.items.forEach((item) => {
            if (item.nodeType !== undefined) {
                lookup.set(item.id, item.nodeType);
            }
        });
    });
    return lookup;
};

/**
 * Resolves a tag's node icon type: the static per-item lookup first, then — for freeform
 * parsedBody/query/headers sub-paths that have no static picker entry — falls back to
 * the owning step's nodeType, same as resolveDisplayLabel.
 */
export const resolveNodeType = (
    inner: string,
    nodeTypeLookup?: NodeTypeLookup,
    stepNodeTypeLookup?: StepNodeTypeLookup
): string | undefined => {
    const direct = nodeTypeLookup?.get(normalizeLookupKey(inner));
    if (direct) return direct;
    if (!stepNodeTypeLookup) return undefined;

    const segments = inner.split(".");
    if (segments.length < 2) return undefined;
    const [stepId, container] = segments;
    if (!HTTP_TRANSPORT_CONTAINER_ROOTS.has(container)) return undefined;
    return stepNodeTypeLookup.get(stepId);
};

const isHttpStepTransportPath = (value: string): boolean => {
    const segments = value.split(".");
    if (HTTP_TRANSPORT_CONTAINER_ROOTS.has(segments[0])) return true;
    return segments.length >= 2 && HTTP_TRANSPORT_CONTAINER_ROOTS.has(segments[1]);
};

export const resolveTagValidation = (
    inner: string,
    lookup: ValidationLookup,
    prefixLookup?: PrefixValidationLookup
): { valid: boolean; paramType: Exclude<TagParamType, undefined> } => {
    const normalized = inner.trim();

    const key = normalizeLookupKey(normalized);
    const direct = lookup.get(key);
    if (direct) return { valid: true, paramType: direct };

    if (prefixLookup) {
        const prefixMatch = prefixLookup.find(({ prefix }) => normalized.startsWith(prefix));
        if (prefixMatch) return { valid: true, paramType: prefixMatch.paramType };
    }

    // HTTP-response transport paths are always valid — do not fall back to matching the base key
    // (e.g. parsedBody.bank_code -> bank_code) against keys/system/steps below.
    if (isHttpStepTransportPath(normalized)) {
        return { valid: true, paramType: "http_steps" };
    }

    const withoutPrefix = normalized.replace(/^(keys|system|steps)\./, "");
    if (withoutPrefix !== normalized) {
        const stripped = lookup.get(normalizeLookupKey(withoutPrefix));
        if (stripped) return { valid: true, paramType: stripped };
    }

    const prefixedCandidates = [`keys.${normalized}`, `system.${normalized}`, `steps.${normalized}`];
    for (const candidate of prefixedCandidates) {
        const matched = lookup.get(normalizeLookupKey(candidate));
        if (matched) return { valid: true, paramType: matched };
    }

    return { valid: false, paramType: inferParamType(normalized) };
};

// ============================================================================
// Mode / normalization helpers
// ============================================================================

export const getEffectiveMaxTags = (mode: TagInputMode, maxTags?: number): number => {
    if (mode === "strict-single") return 1;
    if (typeof maxTags === "number" && maxTags > 0) return maxTags;
    return Number.POSITIVE_INFINITY;
};

export const normalizeValueByMode = (raw: string, mode: TagInputMode, maxTags?: number): string => {
    const effectiveMaxTags = getEffectiveMaxTags(mode, maxTags);

    if (mode === "strict-single") {
        const firstTag = /\{([^{}]+)\}/.exec(raw);
        if (firstTag) return `{${firstTag[1]}}`;
        return raw;
    }

    if (mode === "tag-only") {
        // Don't strip while the user is mid-way through typing {x} — any unclosed { means in-progress
        const withoutCompleteTags = raw.replace(/\{[^{}]+\}/g, "");
        if (withoutCompleteTags.includes("{")) return raw;
        const tags = Array.from(raw.matchAll(/\{([^{}]+)\}/g)).map((match) => match[1]);
        return tags
            .slice(0, effectiveMaxTags)
            .map((inner) => `{${inner}}`)
            .join(" ");
    }

    if (!Number.isFinite(effectiveMaxTags)) return raw;
    let seenTags = 0;
    return raw.replace(/\{([^{}]+)\}/g, (full) => {
        seenTags += 1;
        return seenTags <= effectiveMaxTags ? full : "";
    });
};

// ============================================================================
// Value serialization helpers
// ============================================================================

const isIdentChar = (char: string): boolean => /[A-Za-z0-9_]/.test(char);

/**
 * Finds the index of the "(" at `openIndex`'s matching ")" within `text`, honoring nesting
 * (so `toBool(rand({affs1}))` resolves to the outermost close). Content inside `{...}` tags is
 * skipped so a stray paren-like character in a tag name can never desync the depth count.
 * Returns -1 if unclosed.
 */
const findMatchingParen = (text: string, openIndex: number): number => {
    let depth = 0;
    for (let i = openIndex; i < text.length; i++) {
        const char = text[i];
        if (char === "{") {
            const close = text.indexOf("}", i + 1);
            if (close !== -1) {
                i = close;
                continue;
            }
        }
        if (char === "(") depth++;
        else if (char === ")") {
            depth--;
            if (depth === 0) return i;
        }
    }
    return -1;
};

/** Invisible filler inserted between two atomic pills (TagNode/FunctionPillNode) that would
 * otherwise sit directly adjacent with nothing between them — e.g. "toBool(" immediately followed
 * by ")", or a tag dropped right next to one. contenteditable gives a hoverable/clickable text
 * caret only over real text content, so two back-to-back atomic spans with no text node between
 * them are a dead zone: no cursor, no way to click "between" them. Never part of the reported
 * value — stripped everywhere text is read back out (serializeInlineChildren, flatLength, etc).
 */
export const INLINE_GAP_PLACEHOLDER = "​";

/** A zero-width character has zero rendered pixel area, so the mouse can never be "over" it — no
 * `cursor: text` hover affordance ever shows, even though clicking a pill next to it already
 * resolves correctly (handleContainerMouseDownCapture decides before/after from which half of the
 * clicked pill's own box was hit, not from landing precisely on this node). This gives the span a
 * few real pixels for hover purposes only, then cancels them back out with matching negative
 * margins — margins aren't part of the hover/hit-testing box, only of layout position, so this
 * adds a real hoverable target without adding any visible extra space between pills (confirmed via
 * real Chromium measurement: paired width + negative margins of equal magnitude nets to exactly
 * 0px of added gap between the flanking pills). */
const INLINE_GAP_STYLE = "display: inline-block; width: 4px; margin-left: -2px; margin-right: -2px; cursor: text;";

/** Ensures a real (if invisible) TextNode sits between any two adjacent atomic pills in a flat
 * node list — see INLINE_GAP_PLACEHOLDER. Applied once, by callers, on the fully-flattened result
 * of parseInlineNodes — not inside parseInlineNodes itself, since its recursive calls already
 * splice into one flat list by the time the outermost call returns. */
export const ensureClickableGaps = (nodes: LexicalNode[]): LexicalNode[] => {
    const result: LexicalNode[] = [];
    nodes.forEach((node) => {
        const prev = result[result.length - 1];
        if (prev && !$isTextNode(prev) && !$isTextNode(node)) {
            const gap = $createTextNode(INLINE_GAP_PLACEHOLDER);
            gap.setStyle(INLINE_GAP_STYLE);
            result.push(gap);
        }
        result.push(node);
    });
    return result;
};

// Hover pair-highlighting broadcasts over `document` (see FunctionPillNode's PAIR_HOVER_EVENT),
// which is global across every mounted TagTextInput on the page — a pairId derived only from the
// local "(" offset would collide between two independent inputs that happen to have a call at the
// same offset (e.g. two fields both starting with "toBool("), cross-highlighting unrelated pairs.
// A page-lifetime counter keeps every parsed call's pairId unique regardless of position or which
// editor it's in.
let pairIdCounter = 0;
const nextPairId = (): string => `pair-${pairIdCounter++}`;

/**
 * Parses a single line of raw tag-input text into Lexical nodes: plain text, `{tag}` → TagNode,
 * and `functionName(...)` (for known Functions-tab names) → a pair of flat FunctionPillNode
 * markers ("functionName(" and ")") with the recursively-parsed inner content between them as
 * ordinary siblings — arbitrary nesting (`toBool(rand({affs1}))`) round-trips correctly since the
 * order of the flat markers alone encodes it, no wrapper node needed.
 * A `functionName(` with no matching `)` in `text` is left as plain text.
 */
export const parseInlineNodes = (text: string): LexicalNode[] => {
    const nodes: LexicalNode[] = [];
    let buffer = "";
    let i = 0;

    const flushBuffer = () => {
        if (buffer) {
            nodes.push($createTextNode(buffer));
            buffer = "";
        }
    };

    while (i < text.length) {
        const char = text[i];

        if (char === "\n") {
            flushBuffer();
            nodes.push($createLineBreakNode());
            i += 1;
            continue;
        }

        if (char === "{") {
            const close = text.indexOf("}", i + 1);
            // close === i + 1 means "{}" with nothing between the braces — the tag-only mode
            // placeholder (TAG_ONLY_PLACEHOLDER) the user is meant to type into, not a tag to
            // promote. Leave it as plain text so the caret keeps sitting between the braces.
            if (close !== -1 && close > i + 1 && !text.slice(i + 1, close).includes("{")) {
                flushBuffer();
                nodes.push($createTagNode(text.slice(i + 1, close)));
                i = close + 1;
                continue;
            }
        }

        if (isIdentChar(char) && (i === 0 || !isIdentChar(text[i - 1]))) {
            let j = i;
            while (j < text.length && isIdentChar(text[j])) j++;
            const name = text.slice(i, j);

            if (text[j] === "(" && isKnownFunctionName(name)) {
                const closeIndex = findMatchingParen(text, j);
                const pairId = nextPairId();
                if (closeIndex !== -1) {
                    flushBuffer();
                    // No wrapper node — the open/close pills are flat siblings alongside whatever
                    // sits between them, exactly like a plain tag mixed into surrounding text.
                    // Nesting (toBool(rand({affs1}))) is encoded purely by the left-to-right order
                    // of these flat markers, not by a tree, so every pill behaves — click, drag,
                    // arrow-key nav — identically to a normal TagNode.
                    nodes.push($createFunctionPillNode("open", name, { pairId }));
                    nodes.push(...parseInlineNodes(text.slice(j + 1, closeIndex)));
                    nodes.push($createFunctionPillNode("close", name, { pairId }));
                    i = closeIndex + 1;
                    continue;
                }

                // No matching ")" anywhere in the rest of the text — keep it as a pill instead of
                // degrading to raw text (losing the pill mid-edit is jarring), just flagged
                // unclosed so FunctionPillView can show a warning underline. Whatever follows is
                // parsed as ordinary top-level content by just continuing this same loop — no
                // close marker is emitted, so nesting still round-trips correctly if a later,
                // independently-closed call appears inside the remainder (e.g. "toBool(rand(x)").
                flushBuffer();
                nodes.push($createFunctionPillNode("open", name, { unclosed: true, pairId }));
                i = j + 1;
                continue;
            }
        }

        buffer += char;
        i += 1;
    }

    flushBuffer();
    return nodes;
};

/** Serializes a paragraph's direct children back to raw tag-input text. */
export const serializeInlineChildren = (element: ElementNode): string => {
    let line = "";
    for (const child of element.getChildren()) {
        if ($isFunctionPillNode(child)) {
            line += child.getTextContent();
        } else if ($isTagNode(child)) {
            line += `{${child.__inner}}`;
        } else if ($isLineBreakNode(child)) {
            line += "\n";
        } else if ($isTextNode(child)) {
            line += child.getTextContent().split(INLINE_GAP_PLACEHOLDER).join("");
        }
    }
    return line;
};

export const loadValueIntoEditor = (editor: LexicalEditor, value: string): void => {
    editor.update(() => {
        const root = $getRoot();
        root.clear();

        if (!value) {
            root.append($createParagraphNode());
            return;
        }

        const lines = value.split("\n");
        lines.forEach((line) => {
            const paragraph = $createParagraphNode();
            ensureClickableGaps(parseInlineNodes(line)).forEach((node) => paragraph.append(node));
            root.append(paragraph);
        });
    });
};

export const serializeEditorState = (editorState: EditorState): string => {
    const paragraphs: string[] = [];
    editorState.read(() => {
        const root = $getRoot();
        for (const paragraph of root.getChildren()) {
            if (!$isElementNode(paragraph)) continue;
            paragraphs.push(serializeInlineChildren(paragraph));
        }
    });
    return paragraphs.join("\n");
};
